/**
 * The images filed into this map.
 *
 * A snapshot that cannot be looked at again is a snapshot nobody takes twice,
 * so they sit under the board as a strip: click one to see it whole, take a
 * copy, or remove it — which erases the file as well as the reference, because
 * a genogram picture holds three generations of people who are not patients.
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Download, Loader2, Trash2, X } from "lucide-react";

import { downloadBlob, useMapSnapshots } from "../../application/useMapSnapshots";

interface Props {
    mapId: string | undefined;
    /** How many are filed. Changing it is what makes the strip reload. */
    count: number;
    /** Refreshes the map after one is removed, so the strip follows. */
    onChanged: () => void | Promise<void>;
    editable?: boolean;
}

export default function MapSnapshots({
    mapId,
    count,
    onChanged,
    editable = true,
}: Props) {
    const { t } = useTranslation();
    const { images, loading, remove } = useMapSnapshots(mapId, count);
    const [open, setOpen] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);

    if (count <= 0) return null;

    const shown = images.find((image) => image.index === open) ?? null;

    const nameFor = (index: number) => `genograma-${index + 1}.png`;

    const save = async (url: string, index: number) => {
        const blob = await fetch(url).then((response) => response.blob());
        downloadBlob(blob, nameFor(index));
    };

    const drop = async (index: number) => {
        setBusy(true);
        try {
            await remove(index);
            setOpen(null);
            await onChanged();
        } finally {
            setBusy(false);
        }
    };

    return (
        <section aria-labelledby="systemic-map-snapshots" className="shrink-0">
            <h2
                id="systemic-map-snapshots"
                className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary"
            >
                {t("systemicMap.snapshots.title", { count })}
            </h2>

            {loading && images.length === 0 && (
                <p role="status" className="flex items-center gap-2 text-xs text-content-secondary">
                    <Loader2
                        size={14}
                        aria-hidden="true"
                        className="animate-spin motion-reduce:animate-none"
                    />
                    {t("common.loading")}
                </p>
            )}

            <ul className="flex flex-wrap gap-2">
                {images.map((image) => (
                    <li key={image.index} className="relative">
                        <button
                            type="button"
                            onClick={() => setOpen(image.index)}
                            className="block overflow-hidden rounded-lg border border-border-subtle transition-colors duration-fast hover:border-border-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                            <img
                                src={image.url}
                                alt={t("systemicMap.snapshots.alt", {
                                    n: image.index + 1,
                                })}
                                loading="lazy"
                                className="h-20 w-32 bg-surface-raised object-cover"
                            />
                        </button>
                        {editable && (
                            // On the tile as well as inside the viewer: taking
                            // an image back out should not require opening it
                            // first.
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => void drop(image.index)}
                                aria-label={t("systemicMap.snapshots.removeOne", {
                                    n: image.index + 1,
                                })}
                                className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-lg border border-border-subtle bg-surface-raised/90 text-status-critical shadow-theme-sm transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
                            >
                                <Trash2 size={14} aria-hidden="true" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {shown &&
                createPortal(
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("systemicMap.snapshots.alt", { n: shown.index + 1 })}
                        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-black/70 p-4"
                        onClick={() => setOpen(null)}
                    >
                        <img
                            src={shown.url}
                            alt={t("systemicMap.snapshots.alt", { n: shown.index + 1 })}
                            // Height first, so a small file is scaled up to
                            // fill the room rather than sitting in the middle
                            // of the screen as a stamp.
                            className="h-[78vh] w-auto max-w-full rounded-lg bg-white object-contain shadow-theme-lg"
                            onClick={(event) => event.stopPropagation()}
                        />
                        <div
                            className="flex flex-wrap items-center gap-2"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => void save(shown.url, shown.index)}
                                className="flex min-h-11 items-center gap-2 rounded-lg bg-surface-raised px-4 text-sm font-medium text-content-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <Download size={16} aria-hidden="true" />
                                {t("systemicMap.snapshots.download")}
                            </button>
                            {editable && (
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => void drop(shown.index)}
                                    className="flex min-h-11 items-center gap-2 rounded-lg bg-surface-raised px-4 text-sm font-medium text-status-critical focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
                                >
                                    <Trash2 size={16} aria-hidden="true" />
                                    {t("systemicMap.snapshots.remove")}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setOpen(null)}
                                className="flex min-h-11 items-center gap-2 rounded-lg bg-surface-raised px-4 text-sm font-medium text-content-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <X size={16} aria-hidden="true" />
                                {t("common.close")}
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </section>
    );
}
